import { useCallback, useSyncExternalStore } from 'react';
import { useQueryClient, type QueryCache } from '@tanstack/react-query';

/** Let the first campaign list finish before secondary overview reads.
 * Summary and holds may still be loading: overlap them with the overview
 * instead of creating a second full-table wait. Cache, scope, terminal
 * errors and immediate menu access keep their previous behavior.
 */
export function initialCampaignsSettled(cache: QueryCache, wsId: string, cabinetId: string): boolean {
  const campaigns = cache.findAll({ queryKey: ['campaigns', wsId, cabinetId] });
  const active = campaigns.filter(query => query.getObserversCount() > 0);
  return (active.length ? active : campaigns).some(query => query.state.status !== 'pending');
}

export function isInitialTablePath(pathname: string, wsId: string, cabinetId: string): boolean {
  const path = `/rk/${wsId}/${cabinetId}`;
  return pathname === path || pathname === path + '/';
}

export function useInitialCampaignsSettled(
  wsId: string | undefined, cabinetId: string | undefined, immediate = false,
): boolean {
  const cache = useQueryClient().getQueryCache();
  const subscribe = useCallback((notify: () => void) => cache.subscribe(notify), [cache]);
  const snapshot = useCallback(() => !wsId || !cabinetId || immediate ||
    initialCampaignsSettled(cache, wsId, cabinetId), [cache, wsId, cabinetId, immediate]);
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}
